import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/core/database/supabase';
import type { UserProfile } from '@/core/auth/AuthContext';
import { useToast } from '@/components/ui';

interface ImpersonationContextType {
  impersonatedUser: UserProfile | null;
  isImpersonating: boolean;
  startImpersonation: (userId: string) => Promise<boolean>;
  stopImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationContextType | null>(null);

const CACHE_KEY = 'landscape_impersonated_user';

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const [impersonatedUser, setImpersonatedUser] = useState<UserProfile | null>(null);
  const { toast } = useToast();

  // Khôi phục session mô phỏng nếu F5 trang
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        setImpersonatedUser(JSON.parse(cached));
      }
    } catch { /* ignore */ }
  }, []);

  const startImpersonation = async (userId: string) => {
    try {
      // Fetch user profile from DB
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
        
      if (error || !data) throw new Error('Không thể tải thông tin nhân viên này');
      
      const profile = data as UserProfile;
      setImpersonatedUser(profile);
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(profile));
      localStorage.setItem('lscm_impersonated_id', userId);
      
      toast.success(`Đang mô phỏng giao diện của ${profile.full_name}`);
      // Có thể force reload hoặc để hook tự update
      // Ghi log bảo mật (Audit Log) - bỏ qua lỗi nếu có
      supabase.auth.getUser().then(({ data: authData }) => {
        if (authData?.user) {
          supabase.rpc('log_auth_event', {
            p_user_id: authData.user.id,
            p_action: 'impersonate_start',
            p_summary: `Nhập vai người dùng: ${profile.full_name}`
          }).catch(console.error);
        }
      });

      window.location.href = '/'; // Reset về trang chủ để apply toàn bộ quyền
      return true;
    } catch (err: any) {
      toast.error(err.message || 'Lỗi khi mô phỏng quyền');
      return false;
    }
  };

  const stopImpersonation = () => {
    // Ghi log bảo mật (Audit Log)
    supabase.auth.getUser().then(({ data: authData }) => {
      if (authData?.user) {
        supabase.rpc('log_auth_event', {
          p_user_id: authData.user.id,
          p_action: 'impersonate_stop',
          p_summary: `Dừng nhập vai, trở về quyền gốc`
        }).catch(console.error);
      }
    });

    setImpersonatedUser(null);
    sessionStorage.removeItem(CACHE_KEY);
    localStorage.removeItem('lscm_impersonated_id');
    toast.success('Đã thoát chế độ mô phỏng');
    window.location.href = '/';
  };

  return (
    <ImpersonationContext.Provider value={{
      impersonatedUser,
      isImpersonating: !!impersonatedUser,
      startImpersonation,
      stopImpersonation
    }}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error('useImpersonation must be used within ImpersonationProvider');
  return ctx;
}
